import { CursorTooltipStatus, LassoGuideType, MapEditType } from '../constants';
import { LassoEditHandle, LassoPoint } from '../types';

/**
 * Builds the i18n key for a lasso cursor hint from its status, or `null` when there is no hint.
 * Shared so the MST scheduling model and the orders-page hook resolve the same translation keys.
 */
export const getLassoCursorTooltipKey = (status: CursorTooltipStatus | null): string | null => (
  status ? `cursorTooltip.lassoSelection.${status}` : null
);

export interface LassoEditEffect {
  /** The polygon was just closed, so the tool should switch to edit mode. */
  enterEditing: boolean;
  /**
   * Whether map panning should be toggled for this edit: `false`/`true` while a
   * vertex is being moved/released, `null` when draggability should be untouched.
   */
  nextDraggable: boolean | null;
}

/**
 * Maps a nebula.gl EditableGeoJsonLayer edit type to the shared lasso
 * interaction effects (mode transition + map draggability). Framework-agnostic
 * so both the MST scheduling model and the orders-page hook stay in sync.
 */
export const getLassoEditEffect = (editType: string): LassoEditEffect => {
  let nextDraggable: boolean | null = null;
  if (editType === MapEditType.MOVE_POSITION) {
    nextDraggable = false;
  } else if (editType === MapEditType.FINISH_MOVE_POSITION) {
    nextDraggable = true;
  }

  return {
    enterEditing: editType === MapEditType.ADD_FEATURE,
    nextDraggable,
  };
};

/** Whether a picked lasso object is a polygon vertex (edit handle). */
export const isLassoEditHandle = (object?: LassoEditHandle | null): boolean => (
  object?.properties?.guideType === LassoGuideType.EDIT_HANDLE
);

/**
 * Whether hovering the given vertex would close the polygon: the shape must still be drawing, the
 * vertex must be the first point placed, and enough points must exist to form a polygon. Shared so
 * the MST scheduling model and the orders-page hook show the "closing" hint under identical rules.
 */
export const isLassoClosingPoint = (
  object: LassoEditHandle | undefined | null,
  lassoPoints: LassoPoint[] | undefined,
  isDrawing: boolean,
): boolean => {
  const firstPoint = lassoPoints?.[0];
  const isSameLngLat = !!firstPoint && object?.geometry?.coordinates === firstPoint.position;
  const hasMinimumPoints = (lassoPoints?.length ?? 0) > 2;
  return isDrawing && isLassoEditHandle(object) && hasMinimumPoints && isSameLngLat;
};
